import { c as _c } from "react/compiler-runtime";
import { Badge, Box, Button, Heading, HStack, Icon, Separator, Text, VStack } from '@chakra-ui/react';
import { useIsMobile } from '@src/components/sco/Advisors/hooks/useIsMobile';
import { attentionBadgeProps } from '@src/components/sco/Advisors/utils/advisorsAttention';
import { NestedAppLayout, attachedNavPanelContainerProps, attachedPanelContainerProps } from '@src/components/sco/AppLayout';
import { SidebarNestedNavList, SidebarNestedNavListSection } from '@src/components/sco/SidebarNestedNavList';
import { NextLink } from '@src/components/sco/utilities/NextLink';
import { ReactNode, useMemo } from 'react';
import { IconType } from 'react-icons';
export type SubNavItem = {
  key: string;
  title: string;
  href: string;
  icon: IconType;
  /** Inventory count (muted `countGeneric`, stays local); omitted or 0 renders no badge. */
  count?: number;
  /**
   * Your-turn count (red `count` badge in the `badgeProps` slot, W3). When above 0 it replaces the
   * inventory count on that row (L26); it never comes from inventory.
   */
  attentionCount?: number;
};

/**
 * Inventory count rides on the ReactNode title with the muted `countGeneric` variant. Your-turn
 * counts use `SidebarNestedNavList`'s `badgeProps` slot (`NavItemBadge`) via `attentionBadgeProps`.
 */
const SubNavItemTitle = t0 => {
  const $ = _c(7);
  const {
    title,
    count
  } = t0;
  let t1;
  if ($[0] !== title) {
    t1 = <Text flex={1} lineClamp={1} minWidth={0}>{title}</Text>;
    $[0] = title;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== count) {
    t2 = !!count && <Badge flexShrink={0} variant="countGeneric">{count}</Badge>;
    $[2] = count;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== t1 || $[5] !== t2) {
    t3 = <HStack align="center" gap={2} minWidth={0} width="full">{t1}{t2}</HStack>;
    $[4] = t1;
    $[5] = t2;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  return t3;
};

/** Rail rows for `SidebarNestedNavList`. Exported for stories of a single rail. */
export const advisorsSubNavSections = (items: SubNavItem[], activeKey: string): SidebarNestedNavListSection[] => items.map(item => {
  const badgeProps = attentionBadgeProps(item.attentionCount);
  const inventory = badgeProps ? undefined : item.count;
  return {
    badgeProps,
    href: item.href,
    icon: item.icon,
    isActive: item.key === activeKey,
    title: inventory ? <SubNavItemTitle count={inventory} title={item.title} /> : item.title
  };
});
const NAV_PANEL_ID = 'advisors-subnav-nav';

/**
 * Phone rail (#17): the sub-nav items as one horizontally scrolling row of pills on the page, so the
 * toolbar keeps the only menu toggle. Badges follow the rail rules: your-turn count first (L26),
 * otherwise the muted inventory count.
 */
export const AdvisorsSubNavPills = t0 => {
  const $ = _c(9);
  const {
    items,
    activeKey,
    label
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      scrollbarWidth: "none",
      "&::-webkit-scrollbar": {
        display: "none"
      }
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  let t2;
  if ($[1] !== activeKey || $[2] !== items) {
    let t3;
    if ($[4] !== activeKey) {
      t3 = item => {
        const isActive = item.key === activeKey;
        const attention = attentionBadgeProps(item.attentionCount);
        return <Button key={item.key} asChild={true} flexShrink={0} size="sm" variant={isActive ? "subtle" : "outline"}><NextLink aria-current={isActive ? "page" : undefined} href={item.href}><Icon as={item.icon} boxSize={4} />{item.title}{attention ? <Badge {...attention} /> : item.count ? <Badge variant="countGeneric">{item.count}</Badge> : null}</NextLink></Button>;
      };
      $[4] = activeKey;
      $[5] = t3;
    } else {
      t3 = $[5];
    }
    t2 = items.map(t3);
    $[1] = activeKey;
    $[2] = items;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[6] !== label || $[7] !== t2) {
    t3 = <HStack aria-label={label} as="nav" css={t1} gap={2} marginX={-4} overflowX="auto" paddingX={4} width="calc(100% + 2rem)">{t2}</HStack>;
    $[6] = label;
    $[7] = t2;
    $[8] = t3;
  } else {
    t3 = $[8];
  }
  return t3;
};

/**
 * Second-column layout for Advisors product areas: a sub-navigation rail on the
 * left, section content on the right.
 *
 * Nested AppLayout (same attached-panel pattern as Request Chat / Request Builder)
 * so Opportunities, Sessions, and My Advisors share chrome with the rest of
 * the product instead of a one-off Flex split.
 */
export const AdvisorsSubNavLayout = t0 => {
  const $ = _c(53);
  const {
    railTitle,
    railDescription,
    items,
    activeKey,
    railFooter,
    title,
    description,
    action,
    compactHeader: t1,
    children
  } = t0;
  const compactHeader = t1 === undefined ? false : t1;
  const isMobile = useIsMobile();
  let t2;
  if ($[0] !== activeKey || $[1] !== items) {
    t2 = advisorsSubNavSections(items, activeKey);
    $[0] = activeKey;
    $[1] = items;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const sections = t2;
  const t3 = compactHeader || isMobile ? "xl" : "3xl";
  let t4;
  if ($[3] !== t3 || $[4] !== title) {
    t4 = <Heading size={t3}>{title}</Heading>;
    $[3] = t3;
    $[4] = title;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  let t5;
  if ($[6] !== description || $[7] !== isMobile) {
    t5 = description && <Text color="colorPalette.fg/60" fontSize={isMobile ? "sm" : "md"} maxWidth="6xl">{description}</Text>;
    $[6] = description;
    $[7] = isMobile;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  let t6;
  if ($[9] !== compactHeader || $[10] !== isMobile) {
    t6 = !compactHeader && !isMobile && <Separator marginY={4} width="full" />;
    $[9] = compactHeader;
    $[10] = isMobile;
    $[11] = t6;
  } else {
    t6 = $[11];
  }
  let t7;
  if ($[12] !== t4 || $[13] !== t5 || $[14] !== t6) {
    t7 = <VStack align="start" flex={1} gap={0.5} minWidth={0}>{t4}{t5}{t6}</VStack>;
    $[12] = t4;
    $[13] = t5;
    $[14] = t6;
    $[15] = t7;
  } else {
    t7 = $[15];
  }
  let t8;
  if ($[16] !== action || $[17] !== t7) {
    t8 = <HStack align="end" gap={3} justify="space-between" wrap="wrap">{t7}{action}</HStack>;
    $[16] = action;
    $[17] = t7;
    $[18] = t8;
  } else {
    t8 = $[18];
  }
  const header = t8;
  if (isMobile) {
    let t9;
    if ($[19] !== activeKey || $[20] !== items || $[21] !== railTitle) {
      t9 = <AdvisorsSubNavPills activeKey={activeKey} items={items} label={railTitle} />;
      $[19] = activeKey;
      $[20] = items;
      $[21] = railTitle;
      $[22] = t9;
    } else {
      t9 = $[22];
    }
    let t10;
    if ($[23] !== railFooter) {
      t10 = railFooter ? <Box>{railFooter}</Box> : null;
      $[23] = railFooter;
      $[24] = t10;
    } else {
      t10 = $[24];
    }
    let t11;
    if ($[25] !== children || $[26] !== header || $[27] !== t10 || $[28] !== t9) {
      t11 = <Box flex={1} height="full" minHeight={0} minWidth={0} overflowY="auto" padding={4} width="full"><VStack align="stretch" gap={4} width="full">{header}{t9}{t10}{children}</VStack></Box>;
      $[25] = children;
      $[26] = header;
      $[27] = t10;
      $[28] = t9;
      $[29] = t11;
    } else {
      t11 = $[29];
    }
    return t11;
  }
  let t9;
  if ($[30] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = {
      base: true,
      md: false
    };
    $[30] = t9;
  } else {
    t9 = $[30];
  }
  let t10;
  if ($[31] !== railTitle) {
    t10 = <Heading size="lg">{railTitle}</Heading>;
    $[31] = railTitle;
    $[32] = t10;
  } else {
    t10 = $[32];
  }
  let t11;
  if ($[33] !== railDescription) {
    t11 = railDescription && <Text color="colorPalette.fg/60" fontSize="sm">{railDescription}</Text>;
    $[33] = railDescription;
    $[34] = t11;
  } else {
    t11 = $[34];
  }
  let t12;
  if ($[35] !== t10 || $[36] !== t11) {
    t12 = <VStack align="stretch" gap={1} paddingX={3}>{t10}{t11}</VStack>;
    $[35] = t10;
    $[36] = t11;
    $[37] = t12;
  } else {
    t12 = $[37];
  }
  let t13;
  if ($[38] !== sections) {
    t13 = <SidebarNestedNavList sections={sections} />;
    $[38] = sections;
    $[39] = t13;
  } else {
    t13 = $[39];
  }
  let t14;
  if ($[40] !== railFooter) {
    t14 = railFooter ? <Box>{railFooter}</Box> : null;
    $[40] = railFooter;
    $[41] = t14;
  } else {
    t14 = $[41];
  }
  let t15;
  if ($[42] !== t12 || $[43] !== t13 || $[44] !== t14) {
    t15 = {
      id: NAV_PANEL_ID,
      defaultWidth: 280,
      minWidth: 220,
      maxWidth: 360,
      isResizable: true,
      resizeHandlePosition: "right",
      useDrawerBreakpoints: t9,
      containerProps: attachedNavPanelContainerProps,
      children: <VStack align="stretch" gap={4} height="full" overflowY="auto" paddingX={4} paddingY={4} width="full">{t12}{t13}{t14}</VStack>
    };
    $[42] = t12;
    $[43] = t13;
    $[44] = t14;
    $[45] = t15;
  } else {
    t15 = $[45];
  }
  let t16;
  if ($[46] === Symbol.for("react.memo_cache_sentinel")) {
    t16 = {
      base: 4,
      md: 6,
      lg: 8
    };
    $[46] = t16;
  } else {
    t16 = $[46];
  }
  let t17;
  if ($[47] !== children || $[48] !== header) {
    t17 = {
      id: "advisors-subnav-main",
      containerProps: attachedPanelContainerProps,
      children: <Box flex={1} height="full" minHeight={0} minWidth={0} overflowY="auto" padding={t16} width="full"><VStack align="stretch" gap={4} marginX="auto" maxWidth="6xl" width="full">{header}{children}</VStack></Box>
    };
    $[47] = children;
    $[48] = header;
    $[49] = t17;
  } else {
    t17 = $[49];
  }
  let t18;
  if ($[50] !== t15 || $[51] !== t17) {
    t18 = <NestedAppLayout id="advisors-subnav" panels={[t15, t17]} />;
    $[50] = t15;
    $[51] = t17;
    $[52] = t18;
  } else {
    t18 = $[52];
  }
  return t18;
};